import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import Layout from "components/Layout";
import EmphasisText from "components/EmphasisText";
import LeonardoLanguageToggle from "components/LeonardoLanguageToggle";
import InfoDialog from "components/InfoDialog";
import api from "utils/api";
import { APP_BRAND } from "utils/brand";
import { queueAuthSuccessPopup } from "utils/authSuccessPopup";
import { buildAuthHref, buildRegistrationPayload } from "utils/registration";
import {
  claimCampaignCreditIfEligible,
  getCampaignFromLocation,
  isCampaignCreditOffer,
} from "utils/campaignCredit";
import { getStoredPreferredLanguage } from "utils/preferredLanguage";
import { validatePersonNames } from "utils/personName";
import { useLeonardoLocale } from "scenes/leonardoNikolai/LeonardoLocaleContext";
import { setLogin } from "state";
import "styles/auth.css";

const REGISTRATION_DRAFT_STORAGE_KEY = "drHoffmannRegistrationDraft";
const TOTAL_STEPS = 4;

const STEP_META = [
  { titleKey: "shell.auth.register.stepName.title", leadKey: "shell.auth.register.stepName.lead" },
  { titleKey: "shell.auth.register.stepContact.title", leadKey: "shell.auth.register.stepContact.lead" },
  { titleKey: "shell.auth.register.stepPassword.title", leadKey: "shell.auth.register.stepPassword.lead" },
  { titleKey: "shell.auth.register.stepTerms.title", leadKey: "shell.auth.register.stepTerms.lead" },
];

const initialForm = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  password: "",
  passwordConfirm: "",
  acceptedTerms: false,
  acceptedAgb: false,
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const readRegistrationDraft = () => {
  if (typeof sessionStorage === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(REGISTRATION_DRAFT_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const saveRegistrationDraft = (draft) => {
  if (typeof sessionStorage === "undefined" || !draft) return;
  try {
    sessionStorage.setItem(REGISTRATION_DRAFT_STORAGE_KEY, JSON.stringify(draft));
  } catch {
    /* quota / private mode */
  }
};

const clearRegistrationDraft = () => {
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.removeItem(REGISTRATION_DRAFT_STORAGE_KEY);
  } catch {
    /* ignore */
  }
};

const draftFromForm = (form, step) => ({
  step,
  firstName: form.firstName,
  lastName: form.lastName,
  email: form.email,
  phone: form.phone,
  acceptedTerms: form.acceptedTerms,
  acceptedAgb: form.acceptedAgb,
});

const validateLeonardoNames = (firstName, lastName, t) => {
  const fn = String(firstName || "").trim();
  const ln = String(lastName || "").trim();
  const messages = [];

  if (!fn) messages.push(t("shell.auth.register.name.firstRequired"));
  else if (fn.length < 2) messages.push(t("shell.auth.register.name.firstShort"));
  else if (fn.length > 50) messages.push(t("shell.auth.register.name.firstLong"));

  if (!ln) messages.push(t("shell.auth.register.name.lastRequired"));
  else if (ln.length < 2) messages.push(t("shell.auth.register.name.lastShort"));
  else if (ln.length > 50) messages.push(t("shell.auth.register.name.lastLong"));

  return messages.length ? messages.join(" ") : null;
};

const EDEKA_MEYERS_INTRO_STEPS = [
  "Registriere dich oder melde dich an.",
  "Bestätige deine E-Mail-Adresse, falls du noch nicht registriert bist.",
  "Anschließend werden deinem Konto 20 € gutgeschrieben.",
];

const RegisterPage = ({ title, lead, kicker, introSteps }) => {
  const restoredDraftRef = React.useRef(false);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(initialForm);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { shellT, isLeonardoFlow } = useLeonardoLocale();
  const params = new URLSearchParams(location.search);
  const registration = buildRegistrationPayload(location);
  const next = params.get("next") || "/buchen";
  const campaign = getCampaignFromLocation(location);
  const resolvedIntroSteps =
    Array.isArray(introSteps) && introSteps.length > 0
      ? introSteps
      : isCampaignCreditOffer(campaign)
        ? EDEKA_MEYERS_INTRO_STEPS
        : null;
  const showIntro = Array.isArray(resolvedIntroSteps) && resolvedIntroSteps.length > 0;

  const finishWithSession = async (token, user) => {
    dispatch(setLogin({ token, user }));
    if (isCampaignCreditOffer(campaign)) {
      const credit = await claimCampaignCreditIfEligible(campaign, { token });
      if (credit?.user) {
        dispatch(setLogin({ token, user: credit.user }));
      }
    }
    queueAuthSuccessPopup("login");
    navigate(next, { replace: true });
  };

  const persistDraft = useCallback(
    (nextForm, nextStep) => {
      saveRegistrationDraft(draftFromForm(nextForm, nextStep));
    },
    []
  );

  useEffect(() => {
    if (restoredDraftRef.current) return;
    restoredDraftRef.current = true;
    const draft = readRegistrationDraft();
    if (!draft) return;

    setForm((current) => ({
      ...current,
      firstName: draft.firstName || "",
      lastName: draft.lastName || "",
      email: draft.email || "",
      phone: draft.phone || "",
      acceptedTerms: Boolean(draft.acceptedTerms),
      acceptedAgb: Boolean(draft.acceptedAgb),
      password: "",
      passwordConfirm: "",
    }));
    if (typeof draft.step === "number" && draft.step >= 0 && draft.step < TOTAL_STEPS) {
      setStep(draft.step);
    }
  }, []);

  const handleChange = (key) => (event) => {
    const value = event.target.type === "checkbox" ? event.target.checked : event.target.value;
    setForm((current) => {
      const nextForm = { ...current, [key]: value };
      persistDraft(nextForm, step);
      return nextForm;
    });
  };

  const goToStep = (nextStep) => {
    setError(null);
    setForm((current) => {
      const nextForm =
        nextStep === 2
          ? { ...current, password: "", passwordConfirm: "" }
          : current;
      persistDraft(nextForm, nextStep);
      return nextForm;
    });
    setStep(nextStep);
  };

  const validateNameStep = () => {
    const nameError = isLeonardoFlow
      ? validateLeonardoNames(form.firstName, form.lastName, shellT)
      : validatePersonNames(form.firstName, form.lastName);
    if (nameError) {
      setError(nameError);
      return false;
    }
    return true;
  };

  const validateContactStep = () => {
    const email = String(form.email || "").trim();
    if (!email) {
      setError(shellT("shell.auth.register.emailRequired"));
      return false;
    }
    if (!EMAIL_PATTERN.test(email)) {
      setError(shellT("shell.auth.register.emailInvalid"));
      return false;
    }
    return true;
  };

  const validatePasswordStep = () => {
    if (form.password.length < 8) {
      setError(shellT("shell.auth.register.passwordShort"));
      return false;
    }
    if (form.password !== form.passwordConfirm) {
      setError(shellT("shell.auth.register.passwordMismatch"));
      return false;
    }
    return true;
  };

  const handleNext = (event) => {
    event.preventDefault();
    setError(null);

    if (step === 0 && !validateNameStep()) return;
    if (step === 1 && !validateContactStep()) return;
    if (step === 2 && !validatePasswordStep()) return;

    goToStep(step + 1);
  };

  const handleBack = () => {
    if (step > 0) goToStep(step - 1);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);
    setInfo(null);

    if (!validateNameStep()) {
      setStep(0);
      return;
    }
    if (!validateContactStep()) {
      setStep(1);
      return;
    }
    if (!validatePasswordStep()) {
      setStep(2);
      return;
    }
    if (!form.acceptedTerms) {
      setError(shellT("shell.auth.register.acceptTerms"));
      return;
    }
    if (!form.acceptedAgb) {
      setError(shellT("shell.auth.register.acceptAgb"));
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        phone: form.phone,
        password: form.password,
        ...registration,
        preferredLanguage: getStoredPreferredLanguage(),
        brand: APP_BRAND,
        ...(next ? { verifyReturnTo: next } : {}),
      };
      const { data } = await api.post("/auth/register", payload);
      clearRegistrationDraft();

      if (data.token) {
        setInfo(data.message || null);
        await finishWithSession(data.token, data.user);
        return;
      }
      if (data.emailSent === false || (data.emailSent === undefined && data.user?.emailVerified === false)) {
        setInfo(data.message || shellT("shell.auth.register.verifyEmail"));
      }
      if (data.user?.emailVerified) {
        try {
          const loginRes = await api.post("/auth/login", {
            email: form.email,
            password: form.password,
          });
          await finishWithSession(loginRes.data.token, loginRes.data.user);
          return;
        } catch {
          /* fall through to info path */
        }
      }
      setInfo((current) => current || data.message);
      setForm(initialForm);
      setStep(0);
    } catch (err) {
      setError(err?.response?.data?.message || shellT("shell.auth.register.failed"));
    } finally {
      setSubmitting(false);
    }
  };

  const stepMeta = STEP_META[step];
  const progressPercent = ((step + 1) / TOTAL_STEPS) * 100;

  const renderHeading = () => {
    if (step === 0 && title) return title;
    if (step === 0) return <EmphasisText text={shellT("shell.auth.register.title")} />;
    return shellT(stepMeta.titleKey);
  };

  const stepLead =
    lead && step === 0 ? lead : String(shellT(stepMeta.leadKey) || "").trim();

  const renderStepFields = () => {
    if (step === 0) {
      return (
        <div className="row">
          <div>
            <label htmlFor="reg-first">{shellT("shell.auth.register.firstName")}</label>
            <input
              id="reg-first"
              required
              autoComplete="given-name"
              value={form.firstName}
              onChange={handleChange("firstName")}
            />
          </div>
          <div>
            <label htmlFor="reg-last">{shellT("shell.auth.register.lastName")}</label>
            <input
              id="reg-last"
              required
              autoComplete="family-name"
              value={form.lastName}
              onChange={handleChange("lastName")}
            />
          </div>
        </div>
      );
    }

    if (step === 1) {
      return (
        <>
          <div>
            <label htmlFor="reg-email">{shellT("shell.auth.register.email")}</label>
            <input
              id="reg-email"
              type="email"
              required
              autoComplete="email"
              value={form.email}
              onChange={handleChange("email")}
            />
          </div>
          <div>
            <label htmlFor="reg-phone">{shellT("shell.auth.register.phone")}</label>
            <input
              id="reg-phone"
              type="tel"
              autoComplete="tel"
              placeholder={shellT("shell.auth.register.phonePlaceholder")}
              value={form.phone}
              onChange={handleChange("phone")}
            />
          </div>
        </>
      );
    }

    if (step === 2) {
      return (
        <div className="row">
          <div>
            <label htmlFor="reg-pw">{shellT("shell.auth.register.password")}</label>
            <input
              id="reg-pw"
              type="password"
              required
              autoComplete="new-password"
              minLength={8}
              value={form.password}
              onChange={handleChange("password")}
            />
          </div>
          <div>
            <label htmlFor="reg-pw2">{shellT("shell.auth.register.passwordConfirm")}</label>
            <input
              id="reg-pw2"
              type="password"
              required
              autoComplete="new-password"
              minLength={8}
              value={form.passwordConfirm}
              onChange={handleChange("passwordConfirm")}
            />
          </div>
        </div>
      );
    }

    return (
      <>
        <label className="auth-checkbox-label">
          <input
            type="checkbox"
            checked={form.acceptedTerms}
            onChange={handleChange("acceptedTerms")}
          />
          <span>
            {shellT("shell.auth.register.privacyPrefix")}{" "}
            <Link to="/datenschutz" className="auth-inline-link">
              {shellT("shell.auth.register.privacyLink")}
            </Link>{" "}
            {shellT("shell.auth.register.privacySuffix")}
          </span>
        </label>

        <label className="auth-checkbox-label">
          <input
            type="checkbox"
            checked={form.acceptedAgb}
            onChange={handleChange("acceptedAgb")}
          />
          <span>
            {shellT("shell.auth.register.agbPrefix")}{" "}
            <Link to="/agbs" className="auth-inline-link">
              {shellT("shell.auth.register.agbLink")}
            </Link>{" "}
            {shellT("shell.auth.register.agbSuffix")}
          </span>
        </label>
      </>
    );
  };

  return (
    <Layout>
      <section className={`auth-page${showIntro ? " auth-page--with-intro" : ""}`}>
        <div className="auth-page__inner">
          {showIntro ? (
            <aside className="auth-campaign-intro" aria-label="So funktioniert die Aktion">
              <div className="auth-campaign-intro__kicker">So funktioniert’s</div>
              <ol className="auth-campaign-intro__steps">
                {resolvedIntroSteps.map((text, index) => (
                  <li key={text}>
                    <span className="auth-campaign-intro__step-index" aria-hidden="true">
                      {index + 1}
                    </span>
                    <span className="auth-campaign-intro__step-text">{text}</span>
                  </li>
                ))}
              </ol>
            </aside>
          ) : null}
          <div className="auth-card">
            {isLeonardoFlow && (
              <div className="auth-lang-toggle-row">
                <LeonardoLanguageToggle />
              </div>
            )}
            <div className="auth-kicker">
              {kicker || shellT("shell.auth.register.kicker")}
            </div>

            <div className="auth-step-progress" aria-hidden="true">
              <span className="auth-step-progress-label">
                {shellT("shell.auth.register.stepOf", { current: step + 1, total: TOTAL_STEPS })}
              </span>
              <div className="auth-step-progress-bar">
                <div
                  className="auth-step-progress-fill"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            </div>

            <h1>{renderHeading()}</h1>
            {stepLead ? <p className="auth-lead">{stepLead}</p> : null}

            {error && <div className="auth-alert is-error">{error}</div>}

            <InfoDialog
              open={Boolean(info)}
              title={shellT("shell.auth.register.verifyEmailTitle")}
              message={info || shellT("shell.auth.register.verifyEmailHint")}
              confirmLabel={shellT("shell.auth.register.verifyEmailOk")}
              onClose={() => {
                setInfo(null);
                navigate("/", { replace: true });
              }}
            />

            <form
              className="auth-form"
              onSubmit={step < TOTAL_STEPS - 1 ? handleNext : handleSubmit}
              noValidate
            >
              {renderStepFields()}

              <div className="auth-actions">
                {step > 0 && (
                  <button
                    type="button"
                    className="auth-back"
                    onClick={handleBack}
                    disabled={submitting}
                  >
                    {shellT("shell.auth.register.back")}
                  </button>
                )}
                <button type="submit" className="auth-submit" disabled={submitting}>
                  {submitting
                    ? shellT("shell.auth.register.submitting")
                    : step < TOTAL_STEPS - 1
                      ? shellT("shell.auth.register.next")
                      : shellT("shell.auth.register.submit")}
                </button>
              </div>
            </form>

            <div className="auth-links">
              <span>
                {shellT("shell.auth.register.hasAccount")}{" "}
                <Link
                  to={buildAuthHref(
                    "/einloggen",
                    isCampaignCreditOffer(campaign) || next !== "/buchen"
                      ? { next }
                      : {}
                  )}
                >
                  {shellT("shell.auth.register.loginLink")}
                </Link>
              </span>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default RegisterPage;
